const rtf = new Intl.RelativeTimeFormat("en", {
  localeMatcher: "best fit", // other values: "lookup"
  numeric: "always", // other values: "auto"
  style: "long", // other values: "short" or "narrow"
});


export const getRelativeTimeMessage = (date: Date): string => {
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInMinutes = Math.round(diffInMs / (1000 * 60));

  let relativeTimeMessage = "";

  if (diffInMinutes < 1) {
    relativeTimeMessage = "just now";
  } else if (diffInMinutes < 60) {
    relativeTimeMessage = rtf.format(-diffInMinutes, "minute");
  } else if (diffInMinutes < 1440) { // less than a day
    const hours = Math.round(diffInMinutes / 60);
    relativeTimeMessage = rtf.format(-hours, "hour");
  } else {
    const days = Math.round(diffInMinutes / 1440);
    relativeTimeMessage = rtf.format(-days, "day");
  }

  return relativeTimeMessage;

};

export const trimText = (text: string, trimNewLines: boolean): string => {
  let trimmedText = text.trim();
  if (trimNewLines) {
    trimmedText = trimmedText.replace(/[\r\n]+/g, " ");
  }
  return trimmedText.trim();
};

// Turns identifiers like "JobSync:Batch3" into readable "Job sync - Batch 3" labels.
export const formatSyncStepLabel = (description: string): string => {
  return description
    .split(":")
    .map(segment => {
      const words = segment
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
        .replace(/([a-zA-Z])(\d)/g, "$1 $2")
        .replace(/(\d)([a-zA-Z])/g, "$1 $2")
        .trim();
      const sentence = words.toLowerCase();
      return sentence.charAt(0).toUpperCase() + sentence.slice(1);
    })
    .join(" - ");
};

// If a log message ends with an embedded JSON blob (e.g. "[eventName] {...}"), pretty-print
// just that portion so it's readable instead of a single unbroken line.
export const prettyPrintEmbeddedJson = (message: string): string => {
  const jsonStart = message.search(/[{[]/);
  if (jsonStart === -1) {
    return message;
  }

  const prefix = message.slice(0, jsonStart);
  const jsonPart = message.slice(jsonStart);
  try {
    const parsed = JSON.parse(jsonPart);
    return `${prefix}${JSON.stringify(parsed, null, 2)}`;
  } catch {
    return message;
  }
};

const MONTH_DAY_FORMAT: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };

export const formatMonthDay = (date: Date): string => {
  return date.toLocaleDateString("en-US", MONTH_DAY_FORMAT);
};

const getOrdinalSuffix = (day: number): string => {
  const isTeen = day >= 11 && day <= 13;
  const lastDigit = day % 10;

  if (isTeen) {
    return "th";
  }

  return { 1: "st", 2: "nd", 3: "rd" }[lastDigit] ?? "th";
};

/**
 * A range that collapses to one day reads as a plain date ("September 4th") rather than a
 * degenerate "9/4 - 9/4".
 */
export const formatDateRangeLabel = (start: Date, end: Date): string => {
  if (start.getTime() === end.getTime()) {
    const month = start.toLocaleDateString("en-US", { month: "long" });

    return `${month} ${start.getDate()}${getOrdinalSuffix(start.getDate())}`;
  }

  const endLabel = start.getMonth() === end.getMonth()
    ? `${end.getDate()}`
    : formatMonthDay(end);

  return `${formatMonthDay(start)} - ${endLabel}`;
};